export const CC_MASKS = {
    FOUR_FOUR_FOUR_FOUR: '9999 9999 9999 9999',
    FOUR_SIX_FIVE: '9999 999999 99999?9?9',
    FOUR_SIX_FOUR: '9999 999999 9999?9',
    FOUR_FOUR_FOUR_FOUR_PLUS: '9999 9999 9999 9?9?9?9 ?9?9?9',
    // For unknown cards, anywhere from 8-19 digits:
    // https://www.ansi.org/news_publications/news_story?articleid=da7bcb04-0654-4e03-af54-0e55d50b93a8
    NONE: '9'.repeat(8) + '?9'.repeat(11)
};

export const CARD_TYPES = {
    AMEX: {
        name: 'AMEX',
        issuingNetwork: 'American Express',
        matcher: /^3[47]/,
        lengths: [15, 16, 17],
        securityCode: {
            name: 'CID',
            length: 4
        },
        maskFn: () => CC_MASKS.FOUR_SIX_FIVE
    },
    DISCOVER: {
        name: 'DISCOVER',
        issuingNetwork: 'Discover',
        matcher: /^6(?:011|5|4[4-9]|2212[6-9]|221[3-9]|22[2-8]|229[01]|2292[0-5])/,
        lengths: [16, 17, 18, 19],
        securityCode: {
            name: 'CID',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_FOUR_FOUR_FOUR_PLUS
    },
    MASTER_CARD: {
        name: 'MASTER_CARD',
        issuingNetwork: 'Mastercard',
        matcher: /^(?:5[1-5]|2(?:22[1-8]|229[0-8]|2299|2[3-9]|[3-6]|7[01]|720))/,
        lengths: [16],
        securityCode: {
            name: 'CVC2',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_FOUR_FOUR_FOUR
    },
    VISA: {
        name: 'VISA',
        issuingNetwork: 'Visa',
        matcher: /^4/,
        lengths: [13, 16, 19],
        securityCode: {
            name: 'CVV2',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_FOUR_FOUR_FOUR_PLUS,
    },
    JCB: {
        name: 'JCB',
        issuingNetwork: 'JCB',
        matcher: /^35/,
        lengths: [16],
        securityCode: {
            name: 'CVV2',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_FOUR_FOUR_FOUR,
    },
    DINERS_CLUB: {
        name: 'DINERS_CLUB',
        issuingNetwork: 'Diner\'s Club',
        matcher: /^3(?:0[0-5]|[689])/,
        lengths: [14, 15],
        securityCode: {
            name: 'CVV2',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_SIX_FOUR
    },
    MAESTRO: {
        name: 'MAESTRO',
        issuingNetwork: 'Maestro',
        matcher: /^(?:5(?:018|020|038|893)|6(?:304|759|76[1-3])|0604)/,
        lengths: [16],
        securityCode: {
            name: 'CVV2',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_FOUR_FOUR_FOUR
    },
    LASER: {
        name: 'Laser',
        issuingNetwork: 'Laser',
        matcher: /^6(?:304|706|771|709)/,
        lengths: [16],
        securityCode: {
            name: 'CVV2',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_FOUR_FOUR_FOUR
    },
    SOLO: {
        name: 'Solo',
        issuingNetwork: 'Solo',
        matcher: /^6(?:334|767)/,
        lengths: [16],
        securityCode: {
            name: 'CVV2',
            length: 3
        },
        maskFn: () => CC_MASKS.FOUR_FOUR_FOUR_FOUR
    },
    UNKNOWN: {
        name: 'UNKNOWN',
        issuingNetwork: 'Unknown',
        matcher: /\d+/,
        securityCode: {
            name: 'CVV2',
            length: 4,
            optional: true
        },
        maskFn: () => CC_MASKS.NONE
    }
};

// Cards are ordered from most frequent -> least
// Nearly 70% of cards processed are Visa cards
// 90% are Visa or MasterCard
function* orderedCards() {
    yield* [
        CARD_TYPES.VISA,
        CARD_TYPES.MASTER_CARD,
        CARD_TYPES.AMEX,
        CARD_TYPES.DISCOVER,
        CARD_TYPES.DINERS_CLUB,
        CARD_TYPES.JCB,
        CARD_TYPES.MAESTRO,
        CARD_TYPES.LASER,
        CARD_TYPES.SOLO
    ];
}

export default class CreditCard {
    constructor(_number) {
        Object.assign(this, this._setCardType(_number), { _number });
    }

    // Getters and Setters
    get mask() { return this.maskFn(this._number); }
    get number() { return this._number; }
    get securityCodeMask() { return (this.securityCode.optional ? '?9' : '9').repeat(this.securityCode.length); }
    get valid() { return this._validate(this._number); }

    set number(_number) {
        Object.assign(this, { _number });
        if(this._needsNewCardType(_number)) {
            Object.assign(this, this._setCardType(_number));
        }
    }
    // End Getters and Setters

    _setCardType(number) {
        if(!this._numberAcceptable(number)) return CARD_TYPES.UNKNOWN;
        const cards = orderedCards();
        let found = false;
        let card = cards.next();
        while(!(found || card.done)) {
            found = this._matchNumberToCard(number, card.value);
            card = cards.next();
        }

        return found || CARD_TYPES.UNKNOWN;
    }

    _numberAcceptable(number) {
        return (typeof number === 'string' && number.length && (/^\d+$/).test(number));
    }

    _numberLengthAcceptable(number) {
        return this._numberAcceptable(number) && (!this.lengths || this.lengths.includes(number.length));
    }

    _matchNumberToCard(number, type) {
        if(type.matcher.test(number)) {
            return Object.assign({}, type);
        }
        return false;
    }

    _needsNewCardType(number) {
        const isUnknownCard = this.issuingNetwork === CARD_TYPES.UNKNOWN.issuingNetwork;
        const numberNoLongerMatches = !this.matcher.test(number);
        return isUnknownCard || numberNoLongerMatches;
    }

    _validate(number) {
        return !!(this._numberLengthAcceptable(number) && this._luhn(number));
    }

    _luhn(number) {
        const arr = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];

        let bit = 1;
        let len = number.length;
        let sum = 0;
        let val;

        while (len) {
            val = parseInt(number.charAt(--len), 10);
            sum += (bit ^= 1) ? arr[val] : val; //eslint-disable-line no-bitwise
        }

        return sum && sum % 10 === 0;
    }
}
